import { zodResolver } from "@hookform/resolvers/zod";
import { notifications } from "@marketplace/ui/notifications";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  loginOtpEmailAtom,
  loginOtpExpirationTimeAtom
} from "../../atoms/login-atom";
import type { LoginEmailResponseDto } from "../../dtos/login-email-response-dto";
import { clearOtpSession, readOtpSession } from "../../services/otp-session";
import { usePostVerifyOtpMutation } from "../mutations/use-post-verify-otp-mutation";
import {
  useVerifyOtpSchema,
  type VerifyOtpSchema
} from "../schemas/use-verify-otp-schema";
import { useLogin } from "../use-login";

const routeApi = getRouteApi("/auth/verify");

export const useVerifyOtpForm = () => {
  const navigate = useNavigate();
  const searchParams = routeApi.useSearch();
  const page = searchParams.page;

  const [sessionLoading, setSessionLoading] = useState(true);
  const [email, setEmail] = useAtom(loginOtpEmailAtom);
  const [expirationTime, setExpirationTime] = useAtom(
    loginOtpExpirationTimeAtom
  );

  useEffect(() => {
    (async () => {
      try {
        setSessionLoading(true);
        const session = readOtpSession();
        if (!session) {
          throw new Error("No OTP session found");
        }

        setEmail(session.email);
        setExpirationTime(session.expirationTime);
      } catch {
        notifications.show({
          message: "Invalid token. Please try logging in again.",
          variant: "error"
        });
        navigate({ to: "/auth/login" });
      } finally {
        setSessionLoading(false);
      }
    })();
  }, [navigate, setEmail, setExpirationTime]);

  const { t: tAuth } = useTranslation("auth");
  const verifyOtpSchema = useVerifyOtpSchema();
  const { mutateAsync, isPending } = usePostVerifyOtpMutation();
  const login = useLogin();

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<VerifyOtpSchema>({
    resolver: zodResolver(verifyOtpSchema)
  });

  const handleSignIn = async (data: LoginEmailResponseDto["data"]) => {
    try {
      await login({
        accessToken: data.access_token,
        userData: data.user
      });

      clearOtpSession();

      notifications.show({
        message: tAuth("notification.login.loginSuccess")
      });

      navigate({ to: "/" });
    } catch {
      setError("otp", {
        message: "OTP verification failed. Please try again.",
        type: "server"
      });
    }
  };

  const onSubmit: SubmitHandler<VerifyOtpSchema> = async (values) => {
    try {
      if (!email) return;

      const res = await mutateAsync({
        email,
        otp_code: values.otp,
        page
      });

      if (res && !res.errors) {
        if (page === "register") {
          clearOtpSession();
          notifications.show({
            message: tAuth("notification.register.registerSuccess")
          });
          navigate({ to: "/auth/login" });
        } else if (page === "login") {
          handleSignIn(res.data);
        }
      }
    } catch {
      setError("otp", {
        message: "OTP verification failed. Please try again.",
        type: "server"
      });
    }
  };

  return {
    control,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    isPending,
    sessionLoading,
    email,
    expiredAt: expirationTime
  };
};
